<template>
  <div>
    <!-- Header Section -->
    <div class="row mb-4">
      <div class="col-md-8">
        <div class="d-flex align-items-center">
          <div class="bg-primary-light p-3 rounded-2 me-3">
            <vue-feather type="award" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="24"></vue-feather>
          </div>
          <h3 class="mb-0 fw-bold">Designations Management</h3>
        </div>
      </div>
      <div class="col-md-4">
        <div class="d-flex justify-content-end">
          <button
            v-if="profile.role !== 'user' && profile.role !== 'admin'"
            class="btn btn-primary shadow-sm"
            @click="designationModal = true; designationMode = 'add'"
          >
            <vue-feather type="plus" size="18" class="me-1"></vue-feather>
            Add Designation
          </button>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <Card cardClasses="shadow-sm border-0 h-100 mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="font-size-125 color-gray-600">
              <vue-feather type="list" stroke="#652aa3" size="18" class="me-1"></vue-feather>
              Designations ({{designations.length}})
            </div>
            <div class="bg-primary-light p-2 rounded-circle">
              <vue-feather type="award" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="20"></vue-feather>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table table-hover align-middle">
              <thead class="table-light">
                <tr>
                  <th scope="col" class="fw-semibold">Code</th>
                  <th scope="col" class="fw-semibold">Name</th>
                  <th scope="col" class="fw-semibold">Department</th>
                  <th scope="col" class="fw-semibold">Description</th>
                  <th scope="col" class="fw-semibold">Created</th>
                  <th scope="col" class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody v-if="!loading">
                <tr v-for="designation in designations" :key="designation.id" class="designation-row">
                  <td>
                    <span class="badge bg-primary-light text-primary">{{ designation.code }}</span>
                  </td>
                  <td class="fw-medium">{{ designation.name }}</td>
                  <td>
                    <span v-if="designation.department" class="badge bg-info-light text-dark">{{ designation.department.name }}</span>
                    <span v-else class="text-muted">No department</span>
                  </td>
                  <td>{{ designation.description || 'Not specified' }}</td>
                  <td>{{ formatDate(designation.created_at) }}</td>
                  <td class="text-end">
                    <button
                      v-if="profile.role !== 'user' && profile.role !== 'admin'"
                      class="btn btn-sm btn-outline-primary"
                      @click="designationModal = true; designationMode = 'edit'; populateDesignationEdit(designation)"
                    >
                      <vue-feather type="edit-2" size="16"></vue-feather>
                      Edit
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="loading" class="text-center py-4">
              <Spinner spinner-class="mb-1" />
            </div>
            <div v-if="!loading && designations.length === 0" class="text-center py-4">
              <div class="empty-state">
                <div class="bg-light p-4 rounded-circle mb-3 d-inline-block">
                  <vue-feather type="award" size="32" stroke="#6c757d"></vue-feather>
                </div>
                <h5>No Designations Found</h5>
                <p class="text-muted">Start by adding your first designation</p>
                <button
                  v-if="profile.role !== 'user' && profile.role !== 'admin'"
                  class="btn btn-primary"
                  @click="designationModal = true; designationMode = 'add'"
                >
                  <vue-feather type="plus" size="16" class="me-1"></vue-feather>
                  Add Designation
                </button>
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-center mt-4">
            <button class="btn btn-outline-dark text-uppercase me-4 shadow-sm" @click="$router.go(-1)">
              <vue-feather type="arrow-left" size="16" class="me-1"></vue-feather>
              Back
            </button>
          </div>
        </Card>
      </div>
    </div>

    <!-- Designation Modal -->
    <Modal :open="designationModal" @closed="designationModal = false">
      <div class="modal-header border-0">
        <div class="d-flex align-items-center">
          <h5 class="modal-title m-0 fw-bold" v-if="designationMode === 'add'">Add Designation</h5>
          <h5 class="modal-title m-0 fw-bold" v-else>Edit Designation</h5>
        </div>
        <button @click="designationModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="row g-3">
            <div class="col-md-12 mb-2 h5">
              <label for="code" class="form-label fw-bolder">Designation Code</label>
              <div class="input-group">
                <input
                  type="text"
                  id="code"
                  class="form-control"
                  placeholder="Designation code"
                  v-model="designation.code"
                  readonly
                >
              </div>
              <small class="text-muted fst-italic font-size-75">Code is automatically generated</small>
            </div>
            <div class="col-md-12 mb-2 h5">
              <label for="department" class="form-label fw-bolder">Department <span class="text-danger">*</span></label>
              <div class="input-group">
                <select
                  id="department"
                  class="form-select"
                  v-model="designation.department_id"
                  required
                >
                  <option value="" disabled selected>Select a department</option>
                  <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                    {{ dept.name }}
                  </option>
                </select>
              </div>
            </div>
            <div class="col-md-12 mb-2 h5">
              <label for="name" class="form-label fw-bolder">Designation Name <span class="text-danger">*</span></label>
              <div class="input-group">
                <input
                  type="text"
                  id="name"
                  class="form-control"
                  placeholder="Enter designation name"
                  v-model="designation.name"
                  required
                >
              </div>
            </div>
            <div class="col-md-12 mb-2 h5">
              <label for="description" class="form-label fw-bolder">Description <span class="text-muted">(optional)</span></label>
              <div class="input-group">
                <input
                  type="text"
                  id="description"
                  class="form-control"
                  placeholder="Enter designation description"
                  v-model="designation.description"
                >
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer border-0 pt-0">
        <button
          type="button"
          class="btn btn-outline-secondary"
          data-dismiss="modal"
          @click="designationModal = false"
        >
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          v-if="designationMode === 'add'"
          @click="addDesignation($event)"
        >
          Save Designation
        </button>
        <button
          type="button"
          class="btn btn-primary"
          v-else
          @click="updateDesignation($event, designation.id)"
        >
          Update Designation
        </button>
      </div>
    </Modal>
  </div>
</template>

<script>
import designationService from "@/api/designationService";
import departmentService from "@/api/departmentService";
import formatDate from "../../utils/formatDate";
import {decrypt} from "@/utils/crypto";
import loadingButton from "@/utils/loadingButton";
import VueFeather from 'vue-feather';

export default {
  name: "Designations",
  components: { VueFeather },

  data(){
    return {
      profile: {},
      loading: true,
      designations: [],
      departments: [],
      designationMode: 'add',
      designationModal: false,
      designation: {
        code: '',
        name: '',
        description: '',
        department_id: '',
      },
      nextNo: {},
    }
  },

  methods: {
    formatDate,
    async getDesignations() {
      this.loading = true
      try {
        this.designations = await designationService.getDesignations()
        this.nextNo = await designationService.getDesignationNextNo()
        this.designation.code = this.nextNo.next
      } catch (error) {
        console.error("Error fetching designations:", error)
      } finally {
        this.loading = false
      }
    },

    async getDepartments() {
      try {
        this.departments = await departmentService.getDepartments()
      } catch (error) {
        console.error("Error fetching departments:", error)
      }
    },

    populateDesignationEdit(designation) {
      this.designation = { ...designation }
    },

    async addDesignation(e) {
      if (!this.designation.name || !this.designation.department_id) {
        return
      }

      loadingButton(e)
      try {
        let response = await designationService.createDesignation(this.designation)
        if (response) {
          this.designationModal = false
          await this.getDesignations()
        }
      } catch (error) {
        console.error("Error adding designation:", error)
      } finally {
        loadingButton(e, false)
      }
    },

    async updateDesignation(e, id) {
      if (!this.designation.name || !this.designation.department_id) {
        return
      }

      loadingButton(e)
      try {
        let response = await designationService.updateDesignation(this.designation, id)
        if (response) {
          this.designationModal = false
          await this.getDesignations()
        }
      } catch (error) {
        console.error("Error updating designation:", error)
      } finally {
        loadingButton(e, false)
      }
    }
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    await this.getDepartments()
    await this.getDesignations()
    if(this.$route.query.add)
      this.designationModal = true
  },

  watch: {
    designationModal: function () {
      if (!this.designationModal) {
        this.designation = {
          code: this.nextNo.next,
          name: '',
          description: '',
          department_id: '',
        }
      }
    },
  }
}
</script>

<style scoped>
.bg-primary-light {
  background-color: rgba(101, 42, 163, 0.1);
}

.text-primary {
  color: #652aa3 !important;
}

.bg-info-light {
  background-color: rgba(13, 202, 240, 0.1);
}

.text-info {
  color: #0dcaf0 !important;
}

.form-select {
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  border-radius: 0.25rem;
}

.form-select:focus {
  box-shadow: 0 2px 10px rgba(101, 42, 163, 0.15);
  border-color: #652aa3;
}

.designation-row {
  transition: all 0.2s ease;
}

.designation-row:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.empty-state {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.font-size-125 {
  font-size: 1.25rem;
}

.font-size-75 {
  font-size: 0.75rem;
}

.color-gray-600 {
  color: #6c757d;
}

.card {
  transition: all 0.3s ease;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}

.btn {
  transition: all 0.2s ease;
}

.btn:hover {
  transform: translateY(-2px);
}

.input-group {
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  border-radius: 0.25rem;
}

.input-group:focus-within {
  box-shadow: 0 2px 10px rgba(101, 42, 163, 0.15);
}
</style>
